import cn from 'classnames';
import { useState, useCallback, useEffect, ReactNode } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { toastErrorMessage } from '@utils/utils';
import NavigationMenu from '@components/navigation-menu';
import AppHead from '@components/app-head';
import Footer from '@components/footer';
import useUserLogin from '@hooks/useUserLogin';
import styles from './Layout.module.scss';
import DepositAndWithdrawal from '@modules/DepositWithdrawModal/DepositWithdrawModal';
import RegisterForm from '@modules/auth/register';
import useReduxState from '@hooks/useReduxState';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import Chat from '@components/chat';

interface ILayoutProps {
    children: ReactNode;
    fetched?: boolean;
    title?: string;
    withSpacing?: boolean;
    className?: string;
}

export const DEPOSIT_TAB = 0;
export const WITHDRAW_TAB = 1;

export type TTtransactionModalTabs = typeof DEPOSIT_TAB | typeof WITHDRAW_TAB;
interface ITransactionModal {
    isOpen: boolean;
    selectedTab: TTtransactionModalTabs;
}

export default function Layout({
    children,
    fetched = true,
    title = '',
    withSpacing = false,
    className = '',
}: ILayoutProps): JSX.Element {
    const [loginUser, token, error] = useUserLogin();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [transactionModalOpen, setTransactionModalOpen] =
        useState<ITransactionModal>({
            isOpen: false,
            selectedTab: DEPOSIT_TAB,
        });
    const [{ userData: user, chat }] = useReduxState((state) => state);
    const { setVisible } = useWalletModal();

    const { wallet, connect, connecting, connected } = useWallet();
    const [registerModal, setRegisterModal] = useState<boolean>(false);

    const handleConnectClick = useCallback(async () => {
        setVisible(true);
    }, [connect]);

    const handleRegisterModal = useCallback(
        (state: boolean) => {
            setRegisterModal(state);
        },
        [registerModal],
    );

    useEffect(() => {
        connect();
    }, [wallet]);

    const onWalletConnectionChange = useCallback(async () => {
        if (!localStorage.getItem('token') || (user.id === '' && wallet)) {
            await loginUser();
        }
    }, [connected, wallet]);

    useEffect(() => {
        if (error !== '') {
            if (connected && !token) {
                handleRegisterModal(true);
            }
            toastErrorMessage(error);
        }
    }, [error]);

    const toggleNavigationMenuVisibilityInMobile = useCallback(
        () => setMobileNavOpen((prevState) => !prevState),
        [],
    );

    const openTransactionModal = useCallback(
        (selectedTab: TTtransactionModalTabs) =>
            setTransactionModalOpen({ isOpen: true, selectedTab }),
        [],
    );

    const closeTransactionModal = useCallback(
        () =>
            setTransactionModalOpen((prevState) => ({
                ...prevState,
                isOpen: false,
            })),
        [],
    );

    useEffect(() => {
        onWalletConnectionChange();
    }, [onWalletConnectionChange]);

    /* ClassNames */

    const contentClasses = cn(styles.content__wrapper, className, {
        [styles.withSpacing]: withSpacing,
    });

    return (
        <div className={styles.app}>
            <aside>
                <NavigationMenu
                    isVisibleInMobile={mobileNavOpen}
                    openWithdrawModal={() => openTransactionModal(WITHDRAW_TAB)}
                />
            </aside>

            <main>
                <AppHead
                    {...{
                        connected,
                        connecting,
                        wallet,
                        toggleNavigationMenuVisibilityInMobile,
                        title,
                    }}
                    openDepositModal={() => openTransactionModal(DEPOSIT_TAB)}
                    openWithdrawalModal={() =>
                        openTransactionModal(WITHDRAW_TAB)
                    }
                    connectWallet={handleConnectClick}
                    isNavigationMenuVisible={mobileNavOpen}
                />

                {/* Content */}
                <section
                    className={cn(styles.content, {
                        [styles.chat_collapsed]: chat.collapsed,
                    })}
                >
                    <section className={styles.main}>
                        <section className={contentClasses}>{children}</section>
                        <Footer {...{ fetched }} />
                    </section>
                    <Chat />
                </section>
            </main>
            <DepositAndWithdrawal
                close={closeTransactionModal}
                isOpen={transactionModalOpen.isOpen}
                selectedTabToOpen={transactionModalOpen.selectedTab}
            />
            <RegisterForm
                open={registerModal}
                close={() => handleRegisterModal(false)}
            />
        </div>
    );
}
